import { useEffect, useState } from "react";
import { ShieldCheck, BadgeCheck, MapPinned, PhoneCall, PenLine, IndianRupee, Instagram, MapPin } from "lucide-react";
import { fetchStories, fetchInstagramGallery } from "@/lib/api";
import { CONTACT, IMAGES } from "@/data/siteData";

const SAMPLE_STORIES = [
    {
        type: "College Trip — Manali",
        travellers: "240 Travellers",
        quote: "Planning for 240 engineering students usually takes two months of administrative headache. GhumneChaley locked all coach permits, twin-sharing stays and activity slots in under two weeks.",
        name: "Faculty Coordinator",
        org: "Engineering Institute, Mumbai",
        img: IMAGES.mountains,
    },
    {
        type: "Corporate Offsite — Udaipur",
        travellers: "85 Travellers",
        quote: "Our annual offsite was flawless. The trip captain managed dietary variations and a late flight delay seamlessly. We just showed up and had fun.",
        name: "People Operations Lead",
        org: "Fintech Company, Mumbai",
        img: IMAGES.tajMahal,
    },
];

export function Stories() {
    const [real, setReal] = useState([]);
    useEffect(() => {
        fetchStories().then(setReal).catch(() => {});
    }, []);

    const stories = real.length > 0
        ? real.map((s) => ({ type: s.title, travellers: s.travellers, quote: s.quote, name: s.name, org: s.org, img: s.image || IMAGES.mountains }))
        : SAMPLE_STORIES;

    return (
        <section id="stories" className="py-20 border-b border-stone-300/70" data-testid="stories-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">sachchi kahaaniyan</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">We've Been There. With Them.</h2>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
                    {stories.map((s, i) => (
                        <article key={s.type + i} className="bg-white rounded-2xl border border-stone-300 overflow-hidden flex flex-col" data-testid={`story-card-${i}`}>
                            <div className="relative h-52">
                                <img src={s.img} alt={s.type} loading="lazy" className="h-full w-full object-cover" />
                                {real.length === 0 && (
                                    <span className="absolute top-3 left-3 bg-gold text-navy text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">Sample story — real stories coming soon</span>
                                )}
                                {s.travellers && <span className="absolute bottom-3 left-3 bg-navy/90 text-white text-[10px] font-bold px-2 py-1 rounded">{s.travellers}</span>}
                            </div>
                            <div className="p-6 flex flex-col flex-1">
                                <h3 className="font-display font-black text-lg text-navy">{s.type}</h3>
                                <p className="mt-3 text-sm text-stone-700 leading-relaxed flex-1">"{s.quote}"</p>
                                <div className="mt-5 pt-4 border-t border-stone-200 flex justify-between items-center text-xs">
                                    <span className="font-black text-navy">{s.name}</span>
                                    <span className="text-stone-500">{s.org}</span>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

const GALLERY = [
    { img: IMAGES.heroGroup, alt: "Group celebrating on a trip" },
    { img: IMAGES.mountainLake, alt: "Mountain lake destination" },
    { img: IMAGES.heroBus, alt: "Trip bus ready for departure" },
    { img: IMAGES.camping, alt: "Riverside camping night" },
    { img: IMAGES.collegeFriends, alt: "College friends on tour" },
    { img: IMAGES.hotel, alt: "Group stay property" },
    { img: IMAGES.djNight, alt: "Trip DJ night" },
    { img: IMAGES.groupTravel, alt: "Friends group getaway" },
    { img: IMAGES.dining, alt: "Group dining experience" },
    { img: IMAGES.roadTrip, alt: "Road trip convoy" },
];

export function Gallery() {
    const [ig, setIg] = useState([]);
    useEffect(() => {
        fetchInstagramGallery().then((d) => { if (d.live && d.items.length) setIg(d.items); }).catch(() => {});
    }, []);

    return (
        <section id="gallery" className="py-20 bg-white border-b border-stone-300/70" data-testid="gallery-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">picture proof</p>
                        <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Proof That Trips Are Better Together.</h2>
                        {ig.length > 0 && (
                            <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-pink-600 flex items-center gap-1.5" data-testid="ig-live-badge">
                                <Instagram className="h-3.5 w-3.5" /> Live from @ghumnechaley_holidays
                            </p>
                        )}
                    </div>
                    <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="follow-instagram-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-navy text-navy text-xs font-display font-black uppercase tracking-wider hover:bg-navy hover:text-white transition-colors">
                        <Instagram className="h-4 w-4" /> Follow Our Journey
                    </a>
                </div>

                {ig.length > 0 ? (
                    <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="instagram-grid">
                        {ig.map((p) => (
                            <a key={p.id} href={p.permalink} target="_blank" rel="noreferrer" data-testid={`ig-post-${p.id}`} className="group relative rounded-2xl overflow-hidden border border-stone-200 aspect-square">
                                <img
                                    src={p.media_type === "VIDEO" ? p.thumbnail_url : p.media_url}
                                    alt={(p.caption || "GhumneChaley trip moment").slice(0, 120)}
                                    loading="lazy"
                                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                                <span className="absolute inset-0 bg-navy/0 group-hover:bg-navy/30 transition-colors flex items-center justify-center">
                                    <Instagram className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </span>
                            </a>
                        ))}
                    </div>
                ) : (
                    <div className="mt-10 columns-2 md:columns-3 lg:columns-4 gap-3 [column-fill:_balance]" data-testid="gallery-grid">
                        {GALLERY.map((g, i) => (
                            <img key={i} src={g.img} alt={g.alt} loading="lazy" className={`mb-3 w-full rounded-2xl border border-stone-200 object-cover hover:opacity-90 transition-opacity ${i % 3 === 0 ? "h-64" : i % 3 === 1 ? "h-44" : "h-52"}`} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

const BADGES = [
    { text: "Verified Travel Partners", icon: BadgeCheck },
    { text: "Experienced Trip Managers", icon: ShieldCheck },
    { text: "End-to-End Logistics", icon: MapPinned },
    { text: "24×7 Trip Support", icon: PhoneCall },
    { text: "Customised Itineraries", icon: PenLine },
    { text: "Transparent Pricing", icon: IndianRupee },
];

export function Trust() {
    return (
        <section id="about" className="py-20 border-b border-stone-300/70" data-testid="trust-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">hamari zimmedari</p>
                <h2 className="mt-1 font-display font-black text-3xl sm:text-4xl text-navy tracking-tight">Your Group. Our Responsibility.</h2>
                <p className="mt-4 max-w-2xl text-sm sm:text-base text-stone-600 leading-relaxed">
                    Group travel takes serious planning — permissions, convoys, headcounts, dietary needs and a hundred details
                    nobody sees. GhumneChaley manages all of it behind the scenes, so your group only sees the good part.
                </p>

                <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="trust-badges">
                    {BADGES.map((b) => (
                        <div key={b.text} className="rounded-2xl border border-stone-300 bg-white p-5 text-center" data-testid={`trust-badge-${b.text.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            <b.icon className="h-6 w-6 text-saffron mx-auto" />
                            <div className="mt-3 text-xs font-bold text-navy leading-snug">{b.text}</div>
                        </div>
                    ))}
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="rounded-2xl bg-navy text-white p-7" data-testid="about-intro">
                        <h3 className="font-display font-black text-lg text-gold">Who We Are</h3>
                        <p className="mt-3 text-sm text-white/70 leading-relaxed">
                            Ghumnechaley Holidays — {CONTACT.parent.replace("A brand of ", "a brand of ")} — is a travel and
                            holiday brand dedicated to making every journey memorable, comfortable and hassle-free. We believe
                            travel is about experiencing cultures, creating memories and discovering joy.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-stone-300 bg-white p-7" data-testid="about-vision">
                        <h3 className="font-display font-black text-lg text-navy">Our Vision</h3>
                        <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                            To become a trusted and preferred travel brand by creating meaningful, affordable and unforgettable
                            travel experiences for every traveller.
                        </p>
                    </div>
                    <div className="rounded-2xl border border-stone-300 bg-white p-7" data-testid="about-mission">
                        <h3 className="font-display font-black text-lg text-navy">Our Mission</h3>
                        <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                            To make travel simple, enjoyable, personalised and accessible — delivering quality service and
                            memorable experiences at every step.
                        </p>
                    </div>
                </div>

                <div className="mt-8 rounded-2xl border border-stone-300 bg-cream-dark p-6 flex flex-col md:flex-row md:items-center gap-4 text-sm" data-testid="hq-address">
                    <MapPin className="h-5 w-5 text-saffron shrink-0" />
                    <span className="text-stone-700"><strong className="text-navy">HQ:</strong> {CONTACT.address}</span>
                </div>
            </div>
        </section>
    );
}
